import DeleteOutline from "@mui/icons-material/DeleteOutline";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  SUB_CATEGORY_FIELD_INPUT_TYPES,
  type SubCategoryFieldInputType,
  type TeamFieldConfig,
} from "@entities";
import { teamFieldConfigRepo } from "@repositories";
import { isReservedSubCategoryFieldSlug } from "@shared/helpers/sub-category-field-config";
import { useEffect, useState } from "react";
import { useAlert } from "../../../contexts/AlertProvider";

const FIELD_TYPE_LABELS: Record<SubCategoryFieldInputType, string> = {
  TEXT: "טקסט",
  NUMBER: "מספר",
  BOOLEAN: "כן / לא",
  SELECT: "בחירה מרשימה",
  IMAGE: "תמונה",
};

const FIELD_SLUG_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

type FieldDraft = {
  id: string;
  field: string;
  label: string;
  type: SubCategoryFieldInputType;
  isRequired: boolean;
  defaultValue: string;
  appendToDescription: boolean;
  optionsText: string;
  order: string;
  isVisible: boolean;
};

const EMPTY_DRAFT: FieldDraft = {
  id: "",
  field: "",
  label: "",
  type: "TEXT",
  isRequired: false,
  defaultValue: "",
  appendToDescription: true,
  optionsText: "",
  order: "6",
  isVisible: true,
};

function draftFromRow(row: TeamFieldConfig): FieldDraft {
  return {
    id: row.id,
    field: row.field,
    label: row.label,
    type: row.type,
    isRequired: row.isRequired,
    defaultValue: row.defaultValue ?? "",
    appendToDescription: row.appendToDescription,
    optionsText: (row.options ?? []).join("\n"),
    order: String(row.order ?? 0),
    isVisible: row.isVisible,
  };
}

export function ProjectRequestFieldsEditor({ teamId }: { teamId: string }) {
  const { setAlertText, setSeverity } = useAlert();
  const [rows, setRows] = useState<TeamFieldConfig[]>([]);
  const [draft, setDraft] = useState<FieldDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  async function loadRows() {
    const list = await teamFieldConfigRepo.find({ where: { teamId } });
    setRows([...list].sort((a, b) => a.order - b.order || a.field.localeCompare(b.field)));
  }

  useEffect(() => {
    let cancelled = false;
    teamFieldConfigRepo
      .find({ where: { teamId } })
      .then((list) => {
        if (cancelled) return;
        setRows(
          [...list].sort(
            (a, b) => a.order - b.order || a.field.localeCompare(b.field),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setAlertText("לא הצלחנו לטעון את שדות הפרויקט");
          setSeverity("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [teamId, setAlertText, setSeverity]);

  function updateDraft<K extends keyof FieldDraft>(key: K, value: FieldDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function handleSave() {
    const field = draft.field.trim();
    const label = draft.label.trim();
    const order = Number.parseInt(draft.order, 10);
    const options = draft.optionsText
      .split("\n")
      .map((option) => option.trim())
      .filter(Boolean);

    if (!label) {
      setAlertText("יש להזין תווית לשדה");
      setSeverity("error");
      return;
    }
    if (!FIELD_SLUG_PATTERN.test(field) || isReservedSubCategoryFieldSlug(field)) {
      setAlertText("מזהה השדה צריך להתחיל באות באנגלית ולהכיל רק אותיות ומספרים");
      setSeverity("error");
      return;
    }
    if (rows.some((row) => row.field === field && row.id !== draft.id)) {
      setAlertText("כבר קיים שדה עם המזהה הזה בפרויקט");
      setSeverity("error");
      return;
    }
    if (!Number.isFinite(order)) {
      setAlertText("סדר השדה צריך להיות מספר");
      setSeverity("error");
      return;
    }
    if (draft.type === "SELECT" && options.length === 0) {
      setAlertText("לשדה בחירה צריך לפחות אפשרות אחת");
      setSeverity("error");
      return;
    }

    const appendToDescription =
      draft.type !== "IMAGE" && draft.appendToDescription;
    const payload = {
      teamId,
      field,
      label,
      type: draft.type,
      isRequired: draft.isRequired,
      defaultValue: draft.defaultValue.trim() || null,
      appendToDescription,
      options: draft.type === "SELECT" ? options : [],
      order,
      isVisible: draft.isVisible,
    };

    setSaving(true);
    try {
      if (draft.id) {
        await teamFieldConfigRepo.update(draft.id, payload);
      } else {
        await teamFieldConfigRepo.insert(payload);
      }
      setDraft(EMPTY_DRAFT);
      await loadRows();
      setAlertText("השדה נשמר");
      setSeverity("success");
    } catch {
      setAlertText("שמירת השדה נכשלה");
      setSeverity("error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setSaving(true);
    try {
      await teamFieldConfigRepo.delete({ id });
      if (draft.id === id) setDraft(EMPTY_DRAFT);
      await loadRows();
      setAlertText("השדה נמחק");
      setSeverity("success");
    } catch {
      setAlertText("מחיקת השדה נכשלה");
      setSeverity("error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack
      spacing={1.5}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        שדות נוספים לבקשות סיוע
      </Typography>
      <Typography variant="body2" color="text.secondary">
        השדות האלה יופיעו בכל בקשת סיוע שנפתחת מהקישור של הפרויקט, בלי קשר לקטגוריה שנבחרה.
      </Typography>

      {rows.map((row) => (
        <Stack
          key={row.id}
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <Button
            type="button"
            variant={draft.id === row.id ? "contained" : "text"}
            color="secondary"
            onClick={() => setDraft(draftFromRow(row))}
            sx={{ flex: 1, justifyContent: "flex-start" }}
          >
            {row.label}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
            {FIELD_TYPE_LABELS[row.type]}
          </Typography>
          <IconButton
            type="button"
            aria-label={`מחק ${row.label}`}
            disabled={saving}
            onClick={() => handleDelete(row.id)}
          >
            <DeleteOutline />
          </IconButton>
        </Stack>
      ))}

      <TextField
        color="secondary"
        label="תווית"
        value={draft.label}
        onChange={(event) => updateDraft("label", event.target.value)}
        fullWidth
      />
      <TextField
        color="secondary"
        label="מזהה שדה"
        value={draft.field}
        onChange={(event) => updateDraft("field", event.target.value)}
        helperText="באנגלית, למשל schoolGrade. אותו מזהה כמו עמודת בקשה אם התשובה נשמרת בעמודה."
        fullWidth
      />
      <FormControl fullWidth color="secondary">
        <InputLabel id="project-field-type-label">סוג</InputLabel>
        <Select
          labelId="project-field-type-label"
          label="סוג"
          value={draft.type}
          onChange={(event) =>
            updateDraft("type", event.target.value as SubCategoryFieldInputType)
          }
        >
          {SUB_CATEGORY_FIELD_INPUT_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {FIELD_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        color="secondary"
        label="סדר"
        value={draft.order}
        onChange={(event) => updateDraft("order", event.target.value)}
        helperText="0-4 אחרי סוג הסיוע, 6-10 בין תיאור למיקום, 11 ומעלה אחרי התאריך"
        fullWidth
      />
      <TextField
        color="secondary"
        label="ערך ברירת מחדל"
        value={draft.defaultValue}
        onChange={(event) => updateDraft("defaultValue", event.target.value)}
        fullWidth
      />
      {draft.type === "SELECT" && (
        <TextField
          color="secondary"
          label="אפשרויות"
          value={draft.optionsText}
          onChange={(event) => updateDraft("optionsText", event.target.value)}
          helperText="אפשרות אחת בכל שורה"
          multiline
          minRows={3}
          fullWidth
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            color="secondary"
            checked={draft.isRequired}
            onChange={(event) => updateDraft("isRequired", event.target.checked)}
          />
        }
        label="שדה חובה"
      />
      <FormControlLabel
        control={
          <Checkbox
            color="secondary"
            checked={draft.isVisible}
            onChange={(event) => updateDraft("isVisible", event.target.checked)}
          />
        }
        label="מוצג בטופס"
      />
      <FormControlLabel
        control={
          <Checkbox
            color="secondary"
            checked={draft.type !== "IMAGE" && draft.appendToDescription}
            disabled={draft.type === "IMAGE"}
            onChange={(event) =>
              updateDraft("appendToDescription", event.target.checked)
            }
          />
        }
        label="להוסיף את התשובה לתיאור הבקשה"
      />
      <Stack direction="row" spacing={1}>
        <Button
          type="button"
          variant="contained"
          color="secondary"
          disabled={saving}
          onClick={handleSave}
        >
          {draft.id ? "עדכון שדה" : "הוספת שדה"}
        </Button>
        {draft.id && (
          <Button
            type="button"
            color="secondary"
            disabled={saving}
            onClick={() => setDraft(EMPTY_DRAFT)}
          >
            שדה חדש
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
